import * as _ from "lodash";
import React, { useEffect, useReducer, useState } from "react";
import CommonDropdown from "../../../component/common/CommonDropdown/CommonDropdown";
import ZFDateRangePicker from "../../../component/common/ZFDateRangePicker/ZFDateRangePicker";
import { TDateRange } from "../../../component/common/ZFDateRangePicker/defaultStaticRanges";
import ZFIcon from "../../../component/ui-component/ZFIcon/ZFIcon";
import ZFTabs from "../../../component/ui-component/ZFTabs/ZFTabs";
import ErrorBoundary from "../../../component/utility/ErrorBoundary/ErrorBoundary";
import { ErrorFallback } from "../../../component/utility/ErrorBoundary/ErrorFallback";
import { Loader } from "../../../component/utility/Loader/Loader";
import { TFilterDropDownData } from "../../../fixtures/data-fixtures/ComponentConfigFixtures";
import {
	fetchOrderData,
	fetchOrderFilters,
	fetchOrderLayOut,
} from "../orderDataProvider";
import { OrderListingTable } from "./OrderListingTable/OrderListingTable";
import {
	INIT_ORDER_STATE,
	OrderComponentState,
	orderStateReducer,
	TOrderActionPayLoad,
	TOrderActionType,
} from "./orderStateReducer";
import "./orderTableTabs.scss";
import {
	filterDataTransformation,
	tabConfigTransformation,
	TOrderTableTab,
} from "./OrderModule.utils";
import { DetailedView } from "../detailed-view/DetailedView";

export const TableTabs = () => {
	const [orderState, dispatch] = useReducer(
		orderStateReducer,
		new OrderComponentState(
			INIT_ORDER_STATE.activeTabIndex,
			INIT_ORDER_STATE.tabs
		)
	);

	const [detailViewState, setDetailViewState] = useState({
		visible: false,
		orderShippableId: null,
	});

	const [loadingState, setLoadingState] = useState(true);

	useEffect(() => {
		const y = async () => {
			try {
				setLoadingState(true);
				const layoutConfig = await fetchOrderLayOut();
				const orderModuleTabData: TOrderTableTab[] = tabConfigTransformation(
					layoutConfig
				);

				const initState = {
					type: "INIT" as TOrderActionType,
					payload: { tabs: orderModuleTabData },
				};

				dispatch([initState as TOrderActionPayLoad]);

				initAllTabFilters(orderModuleTabData);
				const reqParams = orderState.getOrderRequestParams({
					tabType: "in_progress",
				});

				await getOrderDetails(reqParams);
			} catch (error) {
				console.error("Failed to fetch data due to", error);
			} finally {
				setLoadingState(false);
			}
		};

		y();
	}, []);

	const getOrderDetails = async (reqParams) => {
		setLoadingState(true);
		try {
			const orderTableData = await fetchOrderData(reqParams);
			const d = {
				type: "TABLE_DATA_UPDATE",
				payload: {
					data: orderTableData,
				},
			};
			dispatch([d as TOrderActionPayLoad]);
		} catch (error) {
			console.log("Error while fetching the data", error);
		} finally {
			setLoadingState(false);
		}
	};

	const initAllTabFilters = async (orderTableTabs: TOrderTableTab[]) => {
		setLoadingState(true);
		orderTableTabs.forEach((tab, tabIndex) => {
			const reqParams = orderState.getOrderFilterRequestParams({
				filterKeys: tab.filters.config.map((f) => f.key),
				tabType: tab.tabType,
			});

			fetchOrderFilters(reqParams)
				.then((orderFilters) => {
					const filtersData = filterDataTransformation(
						tab.filters.config,
						orderFilters.orderFields
					);

					const filters = { ...orderState.getFilters() };

					filters.all = filtersData;
					filters.rest = filtersData;
					dispatch([
						{
							type: "FILTER_UPDATE_BY_TAB",
							payload: { tabIndex, filters },
						},
					]);
					//setLoadingState({ ...loadingState, filters: false });
				})
				.catch((reason) => {
					console.error("Failed to fetch filters due to", reason);
				})
				.finally(() => {
					setLoadingState(false);
				});
		});
	};

	const updateTabFilters = async (
		selectedFilters: TFilterDropDownData[],
		dateRange?: TDateRange
	) => {
		setLoadingState(true);
		try {
			const reqParams = orderState.getOrderFilterRequestParams({
				selectedFilters,
				dateRange,
				tabType: orderState.getActiveTab().tabType,
			});
			const fetchedFilters = await fetchOrderFilters(reqParams);
			const tabFiltersData = { ...orderState.getFilters() };
			const filterConfig = [...orderState.getFilterConfig()];

			const filterData = filterDataTransformation(
				filterConfig,
				fetchedFilters.orderFields
			);

			if (Boolean(tabFiltersData.selected.length)) {
				tabFiltersData.basedOnSelection = filterData;
				tabFiltersData.rest = _.differenceWith(
					[...tabFiltersData.all],
					filterData,
					_.isEqual
				);
			} else {
				if (tabFiltersData.all.length === 0) {
					tabFiltersData.all = filterData;
				}
				tabFiltersData.rest = filterData;
				tabFiltersData.basedOnSelection = [];
			}

			dispatch([
				{
					type: "FILTER_CHANGE",
					payload: { filters: tabFiltersData },
				},
			]);
		} catch (error) {
			console.error("Failed to fetch filters due to", error);
		} finally {
			setLoadingState(false);
		}
		//setLoadingState({ ...loadingState, filters: false });
	};

	const onTabChange = async (tabIndex: number) => {
		dispatch([
			{
				type: "TAB_CHANGE" as TOrderActionType,
				payload: {
					activeTabIndex: tabIndex,
				},
			},
		]);

		const reqParams = orderState.getOrderRequestParams({
			tabIndex,
		});

		await getOrderDetails(reqParams);
	};

	const onFilterChange = async (selectedFilters: TFilterDropDownData[]) => {
		const orderStateFilter = { ...orderState.getFilters() };

		const os =
			selectedFilters.length === 0
				? {
						...orderStateFilter,
						selected: selectedFilters,
						basedOnSelection: [],
						rest: orderStateFilter.all,
				  }
				: { ...orderState.getFilters(), selected: selectedFilters };

		dispatch([
			{
				type: "FILTER_CHANGE" as TOrderActionType,
				payload: {
					filters: os,
				},
			},
		]);

		await updateTabFilters(selectedFilters);

		const reqParams = orderState.getOrderRequestParams({
			selected: selectedFilters,
		});

		await getOrderDetails(reqParams);
	};

	const onPaginationChange = async (itemsPerPage: number, pageNo: number) => {
		dispatch([
			{
				type: "PAGE_PROPERTIES",
				payload: { pageProperties: { pageNo, itemsPerPage } },
			},
		]);

		const reqParams = orderState.getOrderRequestParams({
			pageIndex: pageNo,
			limit: itemsPerPage,
		});

		await getOrderDetails(reqParams);
	};

	const onDateChange = async (dateRangeObj: TDateRange) => {
		const dateRange = (({ startDate, endDate }) => ({
			startDate,
			endDate,
		}))(dateRangeObj);

		dispatch([
			{
				type: "DATE_UPDATE",
				payload: {
					dateRange,
				},
			},
		]);

		const selectedFilters = orderState.getFilters().selected;

		await updateTabFilters(selectedFilters, dateRange);

		const reqParams = orderState.getOrderRequestParams({
			dateRange,
		});

		await getOrderDetails(reqParams);
	};

	const syncData = async () => {
		const reqParams = orderState.getOrderRequestParams({});
		await getOrderDetails(reqParams);
	};

	const onRowClick = (orderShippableId: string) => {
		setDetailViewState({
			...detailViewState,
			visible: true,
			orderShippableId,
		});
	};

	const showListingPage = async () => {
		setDetailViewState({ ...detailViewState, visible: false });
		const reqParams = orderState.getOrderRequestParams({
			selected: orderState.getFilters().selected,
		});
		await getOrderDetails(reqParams);
	};

	return (
		<>
			{detailViewState.visible && (
				<div
					className="react-component"
					style={{ backgroundColor: "#f6f7f7" }}
				>
					<div className="zf-padding-t-m order-module-container">
						<div className="page-hdr">
							<ErrorBoundary FallbackComponent={ErrorFallback}>
								<DetailedView
									onBackButton={showListingPage}
									orderShippableId={
										detailViewState.orderShippableId
									}
								/>
							</ErrorBoundary>
						</div>
					</div>
				</div>
			)}
			{!detailViewState.visible && (
				<div
					className="react-component"
					style={{ backgroundColor: "#f6f7f7" }}
				>
					<div className="zf-padding-t-m order-module-container">
						<div className="page-hdr">
							<span className="zf-text-font-size-xl zf-text-color-black">
								Orders
							</span>
							<div className="align-item zf-margin-t-m">
								<span className="zf-text-font-size-xs zf-text-color-black-80">
									Last updated on{" "}
									{orderState.getLastUpdateTime()}{" "}
								</span>
								<span onClick={() => syncData()}>
									<ZFIcon
										iconName="sync"
										iconClasses={[
											"zf-icon-react--size-md zf-icon-react--bg-cover zf-margin-l-m",
										]}
									/>
								</span>
							</div>
						</div>
						<div
							className="zf-padding-t-m zf-padding-l-xl zf-padding-r-xl zf-margin-t-xxl-1 zf-comp-card"
							style={{ minHeight: "calc(100vh - 112px)" }}
						>
							{loadingState ? (
								<div className="config-loader__center">
									<Loader size={"L_LARGE"} />
								</div>
							) : (
								<>
									<ZFTabs
										activeTabIndex={
											orderState.activeTabIndex
										}
										onTabChange={onTabChange}
										tabs={orderState.getAllTabs()}
									/>
									<div className="flter-date-row filter-wrapper">
										<ErrorBoundary
											FallbackComponent={ErrorFallback}
										>
											<div className="align-item align-item__both-end">
												<CommonDropdown
													key={
														orderState.activeTabIndex
													}
													configs={orderState.getFilterConfig()}
													basedOnSelection={
														orderState.getFilters()
															.basedOnSelection
													}
													rest={
														orderState.getFilters()
															.rest
													}
													selected={
														orderState.getFilters()
															.selected
													}
													onFilterChange={
														onFilterChange
													}
												/>
											</div>
										</ErrorBoundary>

										{!Boolean(
											orderState.getDateProperties()
												.hidden
										) && (
											<ZFDateRangePicker
												key={orderState.activeTabIndex}
												dateProps={orderState.getDateProperties()}
												onDateChange={onDateChange}
											/>
										)}
									</div>

									<ErrorBoundary
										FallbackComponent={ErrorFallback}
									>
										<OrderListingTable
											key={orderState.activeTabIndex}
											headers={
												orderState.getActiveTab().table
													.config.headers
											}
											orderListingConfig={
												orderState.getActiveTab().table
													.config.orderListingConfig
											}
											onPaginationChange={
												onPaginationChange
											}
											pageProperties={orderState.getTabPageProperties()}
											onRowClick={onRowClick}
											enableDetailView={
												orderState.getActiveTab()
													.enableDetailView
											}
										/>
									</ErrorBoundary>
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
};
